import is from '@sindresorhus/is';
import { useEffect, useMemo, useState } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import type { LanguageCodeForJSA } from '~/shared/internationalization';
import type { TranslationKey } from '~/shared/internationalization/translation';

import { useJobArchitectureContentLanguage } from '../providers/JobArchitectureLanguageProvider';

const getLanguages = (
    item: { readonly translations: readonly { readonly language: LanguageCodeForJSA }[] } | undefined,
): readonly LanguageCodeForJSA[] => {
    return item?.translations.map((translation) => translation.language) ?? [];
};

const getCommonLanguages = (
    items: readonly ({ readonly translations: readonly { readonly language: LanguageCodeForJSA }[] } | undefined)[],
): readonly LanguageCodeForJSA[] => {
    if (is.emptyArray(items)) {
        return [];
    }

    const languageSets = items.map((item) => new Set(getLanguages(item)));
    const firstSet = languageSets[0];

    if (is.undefined(firstSet)) {
        return [];
    }

    return [...firstSet].filter((language) => languageSets.every((set) => set.has(language)));
};

export const useSetupLanguage = (
    preferredLanguage?: LanguageCodeForJSA | undefined,
    ...items: readonly ({ readonly translations: readonly { readonly language: LanguageCodeForJSA }[] } | undefined)[]
): {
    readonly activeLanguage: LanguageCodeForJSA;
    readonly activeLanguageIndex: number;
    readonly availableLanguages: readonly { readonly id: LanguageCodeForJSA; readonly name: string }[];
    readonly setActiveLanguage: (language: LanguageCodeForJSA) => void;
} => {
    const translate = useTranslate();
    const contentLanguage = useJobArchitectureContentLanguage();

    const availableLanguages = useMemo(
        () =>
            getCommonLanguages(items).map((language) => ({
                id: language,
                name: translate(`common.languages.${language}` as TranslationKey),
            })),
        [items, translate],
    );

    const initialLanguage = preferredLanguage ?? contentLanguage;
    const [activeLanguage, setActiveLanguage] = useState<LanguageCodeForJSA>(initialLanguage);

    const activeLanguageIndex = availableLanguages.findIndex((language) => language.id === activeLanguage);

    useEffect(() => {
        if (is.undefined(preferredLanguage)) {
            return;
        }
        if (!availableLanguages.some((language) => language.id === preferredLanguage)) {
            return;
        }
        setActiveLanguage((current) => (current === preferredLanguage ? current : preferredLanguage));
    }, [availableLanguages, preferredLanguage]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: suppressed due to migration
    useEffect(() => {
        if (availableLanguages.length === 0 || availableLanguages.some((language) => language.id === activeLanguage)) {
            return;
        }

        setActiveLanguage(availableLanguages[0]?.id ?? 'en-US');
    }, [availableLanguages, items, contentLanguage, activeLanguage]);

    return {
        activeLanguage,
        activeLanguageIndex,
        availableLanguages,
        setActiveLanguage,
    };
};
